import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, call, localToday, planPath, toApiError, type GetToken, type PlanResponse } from './api';
import type { EditRequest } from './components/UnitRow';

/**
 * One plan as the Worker stores it, shared by the editor and the weekly view. Every change is
 * one request that answers with the plan, and requests never overlap, so edits apply in order and
 * the browser never holds a plan the Worker did not store.
 */
export function usePlan(planId: string, getToken: GetToken) {
  const [data, setData] = useState<PlanResponse | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  useEffect(() => {
    let live = true; const controller = new AbortController(); setLoadError(null);
    void call<PlanResponse>(getToken, `${planPath(planId)}?today=${localToday()}`, { signal: controller.signal })
      .then(response => { if (live) setData(response); })
      .catch(failure => { if (live) setLoadError(toApiError(failure)); });
    return () => { live = false; controller.abort(); };
  }, [planId, getToken, attempt]);

  /** The saved plan, or null when the request was refused, failed, or another was in flight. */
  const send = useCallback(async (path: string, method: string, body: object): Promise<PlanResponse | null> => {
    if (inFlight.current) return null;
    inFlight.current = true; setBusy(true); setError(null);
    try {
      const response = await call<PlanResponse>(getToken, path, { method, body: JSON.stringify({ ...body, today: localToday() }) });
      setData(response);
      return response;
    } catch (failure) {
      setError(`We could not save that change. ${toApiError(failure).message}`);
      return null;
    } finally { inFlight.current = false; setBusy(false); }
  }, [getToken]);
  const edit = useCallback((change: EditRequest) => send(`${planPath(planId)}/edits`, 'POST', { edit: change }), [send, planId]);
  const retry = useCallback(() => setAttempt(n => n + 1), []);
  return { data, loadError, retry, busy, error, send, edit };
}
